import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarClock, ListTodo, RefreshCcw } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection } from '@/components/common/FormBits'
import { DateField } from '@/components/ui/date-field'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useSaveFollowup, useSaveRenewal } from '../../api'
import { CHANNELS, RENEWAL_KINDS, RENEWAL_STATUSES, type Followup, type Renewal } from '../../types'
import { toastError, useCrm } from '../../ui'
import { Shell } from './RecordForms'

const today = () => new Date().toISOString().slice(0, 10)
const plusMonths = (n: number) => {
  const d = new Date()
  d.setMonth(d.getMonth() + n)
  return d.toISOString().slice(0, 10)
}

export function RenewalForm({
  customerId,
  renewal,
  onDone,
}: {
  customerId: string
  renewal: Renewal | null
  onDone: () => void
}) {
  const { t } = useTranslation()
  const save = useSaveRenewal()
  const schema = z.object({
    kind: z.enum(RENEWAL_KINDS),
    dueOn: z.string().min(1, t('crm.form.dateRequired')),
    amount: z.string().regex(/^\d+(\.\d{1,2})?$/, t('crm.form.amountInvalid')),
    status: z.enum(RENEWAL_STATUSES),
    notes: z.string().max(500),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      kind: renewal?.kind ?? 'license',
      dueOn: renewal?.dueOn ?? plusMonths(12),
      amount: renewal?.amount ?? '0.00',
      status: renewal?.status ?? 'upcoming',
      notes: renewal?.notes ?? '',
    },
  })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: renewal?.id, input: { ...v, customerId, orderId: renewal?.orderId ?? null } })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell
      icon={<RefreshCcw />}
      title={t(renewal ? 'crm.renewal.editTitle' : 'crm.renewal.createTitle')}
      hint={t('crm.renewal.hint')}
      pending={save.isPending}
      onDone={onDone}
      onSubmit={(e) => void onSubmit(e)}
    >
      <FormSection>
        <Field id="rn-kind" label={t('crm.renewal.kind')}>
          <FormSelect
            id="rn-kind"
            control={form.control}
            name="kind"
            options={RENEWAL_KINDS.map((k) => ({ value: k, label: t(`crm.renewalKind.${k}`) }))}
          />
        </Field>
        <Field id="rn-due" label={t('crm.renewal.dueOn')} error={errors.dueOn?.message}>
          <Controller
            control={form.control}
            name="dueOn"
            render={({ field }) => (
              <DateField id="rn-due" value={field.value} onChange={field.onChange} clearable={false} />
            )}
          />
        </Field>
        <Field
          id="rn-amount"
          label={t('crm.renewal.amount')}
          error={errors.amount?.message}
          hint={t('crm.renewal.amountHint')}
        >
          <Controller
            control={form.control}
            name="amount"
            render={({ field }) => (
              <NumberInput
                id="rn-amount"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                minDecimalsOnBlur={2}
                suffix="₺"
              />
            )}
          />
        </Field>
        <Field id="rn-status" label={t('crm.fields.status')}>
          <FormSelect
            id="rn-status"
            control={form.control}
            name="status"
            options={RENEWAL_STATUSES.map((s) => ({ value: s, label: t(`crm.renewalStatus.${s}`) }))}
          />
        </Field>
        <Field id="rn-notes" label={t('crm.fields.note')} full>
          <Textarea id="rn-notes" rows={3} maxLength={500} {...form.register('notes')} />
        </Field>
      </FormSection>
    </Shell>
  )
}

/** `customerId` verilmezse müşteri formda seçilir (genel Takipler sayfası). */
export function FollowupForm({
  customerId,
  followup,
  onDone,
}: {
  customerId?: string
  followup: Followup | null
  onDone: () => void
}) {
  const { t } = useTranslation()
  const save = useSaveFollowup()
  const { staff, customers } = useCrm()
  const schema = z.object({
    customerId: z.string().min(1, t('crm.pickCustomer')),
    channel: z.enum(CHANNELS),
    dueOn: z.string().min(1, t('crm.form.dateRequired')),
    ownerId: z.string().min(1, t('crm.form.staffRequired')),
    note: z.string().trim().min(3, t('crm.followup.noteRequired')).max(300),
  })
  const owner = customers.find((c) => c.id === customerId)?.ownerId
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      customerId: followup?.customerId ?? customerId ?? '',
      channel: followup?.channel ?? 'phone',
      dueOn: followup?.dueOn ?? today(),
      ownerId: followup?.ownerId ?? owner ?? staff[0]?.id ?? '',
      note: followup?.note ?? '',
    },
  })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: followup?.id, input: v })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell
      icon={followup ? <CalendarClock /> : <ListTodo />}
      title={t(followup ? 'crm.followup.editTitle' : 'crm.followup.createTitle')}
      hint={t('crm.followup.hint')}
      pending={save.isPending}
      onDone={onDone}
      onSubmit={(e) => void onSubmit(e)}
    >
      <FormSection>
        {customerId || followup ? null : (
          <Field id="fu-customer" label={t('crm.fields.customer')} error={errors.customerId?.message} full>
            <FormSelect
              id="fu-customer"
              control={form.control}
              name="customerId"
              placeholder={t('crm.pickCustomer')}
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
        )}
        <Field id="fu-channel" label={t('crm.fields.channel')}>
          <FormSelect
            id="fu-channel"
            control={form.control}
            name="channel"
            options={CHANNELS.map((c) => ({ value: c, label: t(`crm.channel.${c}`) }))}
          />
        </Field>
        <Field id="fu-due" label={t('crm.followup.dueOn')} error={errors.dueOn?.message}>
          <Controller
            control={form.control}
            name="dueOn"
            render={({ field }) => (
              <DateField id="fu-due" value={field.value} onChange={field.onChange} clearable={false} />
            )}
          />
        </Field>
        <Field id="fu-owner" label={t('crm.followup.owner')} error={errors.ownerId?.message} full>
          <FormSelect
            id="fu-owner"
            control={form.control}
            name="ownerId"
            options={staff.map((s) => ({ value: s.id, label: s.name, description: s.title }))}
          />
        </Field>
        <Field id="fu-note" label={t('crm.followup.note')} error={errors.note?.message} full>
          <Textarea
            id="fu-note"
            rows={3}
            maxLength={300}
            placeholder={t('crm.followup.notePlaceholder')}
            {...form.register('note')}
          />
        </Field>
      </FormSection>
    </Shell>
  )
}
